import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Box, Button, CircularProgress, Grid, Typography } from '@mui/joy';
import { observer } from 'mobx-react-lite';

import {
  SystemHealthSummary,
  RecentCampaignHighlights,
  CriticalAlertsPanel,
  UpcomingScheduledCampaigns,
  PerformanceMetrics,
  WeatherImpactIndicator,
  QuickAccessShortcuts,
  MaverickAIChat
} from '../components/DashboardBeta';
import ResponsiveLayout from '../components/layout/ResponsiveLayout';
import TableHeader from '../components/TableHeader';
import { Device, DeviceHeartBeat } from '../models/Device';
import { DeviceService } from '../services/DeviceService';
import organizationStore from '../stores/OrganizationStore';
import { DeviceCategories } from '../types/DeviceCategories';

// Import new dashboard components

const DashboardBeta = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedOrg } = organizationStore;

  const [devices, setDevices] = useState<Device[]>([]);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [heartbeats, setHeartbeats] = useState<{ [deviceId: string]: DeviceHeartBeat | null }>({});

  useEffect(() => {
    let cancelled = false;

    const fetchDevices = async () => {
      if (!selectedOrg) {
        setError(t('drones.error_no_org'));
        setInitialLoading(false);
        return;
      }
      setError(null);
      try {
        // Load registered devices then filter to DRONE category (drone-centric dashboard)
        const res = await DeviceService.getAllDevices();
        if (cancelled) return;
        if (!res.success) {
          throw new Error('Failed to fetch devices');
        }

        const allDevices = res.data || [];
        const droneDevices = allDevices.filter((d) => d.device_type?.category === DeviceCategories.Drone);
        setDevices(droneDevices);

        // Build a heartbeat map using included heart_beat, avoid extra calls for now
        const hbEntries: [string, DeviceHeartBeat | null][] = droneDevices.map((d) => [d.id, d.heart_beat ?? null]);
        setHeartbeats(Object.fromEntries(hbEntries));
      } catch (err) {
        if (!cancelled) {
          setError(t('drones.error_fetch'));
        }
      } finally {
        if (!cancelled) {
          setInitialLoading(false);
        }
      }
    };
    fetchDevices();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg]);

  // Helper function to determine connection status
  const getConnectionStatus = (heartbeat: DeviceHeartBeat | null) => {
    if (!heartbeat) return { status: 'offline', color: 'danger' };

    // Check how recent the heartbeat is
    const lastHeartbeat = new Date(heartbeat.timestamp);
    const now = new Date();
    const diffMinutes = (now.getTime() - lastHeartbeat.getTime()) / (1000 * 60);

    if (diffMinutes < 5) return { status: 'online', color: 'success' };
    if (diffMinutes < 30) return { status: 'idle', color: 'warning' };
    return { status: 'offline', color: 'danger' };
  };

  if (initialLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  // These variables are used in the hidden original dashboard section
  // Keeping them commented for reference
  // const selectedDroneData = drones.find(d => d.id === selectedDrone);
  // const selectedHeartbeat = selectedDrone ? heartbeats[selectedDrone] : null;
  // If we restore the original dashboard, we'll need to uncomment the getBatteryStatus function above
  // const batteryStatus = getBatteryStatus(selectedHeartbeat);
  // const connectionStatus = getConnectionStatus(selectedHeartbeat);

  return (
    <ResponsiveLayout>
      {error && (
        <Typography color='danger' level='body-lg' sx={{ textAlign: 'center', mt: 2 }}>
          {error}
        </Typography>
      )}
      <TableHeader title={t('dashboard.beta_title') || 'Dashboard (Beta)'}>
        <Box sx={{ display: 'flex', alignItems: 'center', marginLeft: 'auto', gap: 1 }}>
          <Button color='neutral' onClick={() => navigate('/register-device')} variant='solid'>
            {t('drones.add_device', 'Add Device')}
          </Button>
        </Box>
      </TableHeader>

      <Grid container spacing={2} sx={{ mt: 1 }}>
        {/* Main Grid Container - All components */}
        <Grid container spacing={2}>
          <Grid lg={12} md={12} xs={12}>
            <QuickAccessShortcuts />
          </Grid>
          {/* First Row */}
          <Grid lg={4} md={6} xs={12}>
            <SystemHealthSummary
              offlineDrones={
                devices.filter((d) => getConnectionStatus(heartbeats[d.id] ?? null).status === 'offline').length
              }
              onlineDrones={
                devices.filter((d) => getConnectionStatus(heartbeats[d.id] ?? null).status === 'online').length
              }
              totalDrones={devices.length}
              warningDrones={
                devices.filter((d) => getConnectionStatus(heartbeats[d.id] ?? null).status === 'idle').length
              }
            />
          </Grid>

          <Grid lg={4} md={6} xs={12}>
            <CriticalAlertsPanel />
          </Grid>

          <Grid lg={4} md={12} xs={12}>
            <MaverickAIChat />
          </Grid>

          {/* Third Row */}
          <Grid lg={6} md={6} xs={12}>
            <RecentCampaignHighlights />
          </Grid>

          <Grid lg={6} md={6} xs={12}>
            <UpcomingScheduledCampaigns />
          </Grid>
          <Grid lg={12} md={12} xs={12}>
            <WeatherImpactIndicator />
          </Grid>
          <Grid lg={12} md={12} xs={12}>
            <PerformanceMetrics />
          </Grid>
        </Grid>
      </Grid>
    </ResponsiveLayout>
  );
});

export default DashboardBeta;
